import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "./supabase";
import { C, font } from "./config/theme";
import { IcoDash, IcoHelp, IcoSettings, IcoUsers, IcoChantier, IcoDirection, IcoCommercial, IcoRH, IcoAlertes } from "./components/Icons";
import Sidebar from "./components/Sidebar";
import PageCA from "./pages/PageCA";
import PagePilotageChantier from "./pages/PagePilotageChantier";
import PageDirection from "./pages/PageDirection";
import PageCommercial from "./pages/PageCommercial";
import PageRH from "./pages/PageRH";
import PageAlertes from "./pages/PageAlertes";
import PageAide from "./pages/PageAide";
import PageParametres from "./pages/PageParametres";
import PageEquipe from "./pages/PageEquipe";
import StatusMessage from "./components/StatusMessage";


const NAV_ALL = [
  { id: "direction", label: "Direction", icon: IcoDirection, roles: ["admin", "dirigeant"] },
  { id: "ca", label: "Chiffre d'affaires", icon: IcoDash },
  { id: "commercial", label: "Commercial", icon: IcoCommercial, roles: ["admin", "dirigeant"] },
  { id: "pilotage-chantier", label: "Pilotage chantier", icon: IcoChantier },
  { id: "rh", label: "RH / Main d'œuvre", icon: IcoRH, roles: ["admin", "dirigeant"] },
  { id: "alertes", label: "Alertes", icon: IcoAlertes, roles: ["admin", "dirigeant"] },
  { id: "equipe", label: "Mon équipe", icon: IcoUsers, roles: ["admin", "dirigeant"] },
  { id: "parametres", label: "Paramètres", icon: IcoSettings, roles: ["admin", "dirigeant"] },
  { id: "aide", label: "Aide KPI", icon: IcoHelp },
];

/**
 * Layout principal Bati Viz — MCD v14
 *
 * Reçoit de App.js :
 * - session, profile, tenant, tenantId, role, signOut
 *
 * Chaque page (PageCA, etc.) est autonome et charge ses propres données
 * via les RPCs bv_rpc_* avec RLS automatique (tenant filtré côté serveur).
 */
export default function Dashboard({ session, profile, tenant, tenantId, role, signOut }) {
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activePage, setActivePage] = useState("ca");

  // ─── Config client (pour PageParametres + nom sidebar) ───
  const [clientConfig, setClientConfig] = useState({});
  const [loadError, setLoadError] = useState(null);

  // ─── Nom client pour la sidebar ───
  const clientNom = useMemo(() => {
    return clientConfig.gen_nom || tenant?.nom || "";
  }, [clientConfig, tenant]);

  // ─── Navigation filtrée par rôle ───
  const NAV = useMemo(() =>
    NAV_ALL.filter(item => !item.roles || item.roles.includes(role)),
    [role]
  );

  // ─── Chargement initial ───
  useEffect(() => {
    if (!tenantId) return;

    async function load() {
      setLoading(true);
      try {
        // Charger la config client (bv_kpi_rules) pour le nom + paramètres
        const { data, error } = await supabase
          .from("bv_kpi_rules")
          .select("code_regle, valeur")
          .eq("actif", true);

        if (data) {
          const map = {};
          data.forEach((r) => (map[r.code_regle] = r.valeur));
          setClientConfig(map);
        }
        if (error) console.error("Erreur chargement config:", error);

        // Titre de la page
        document.title = `Bati Viz — ${tenant?.nom || ""}`;
      } catch (err) {
        console.error("Erreur chargement:", err);
        setLoadError("Impossible de charger les données. Vérifiez votre connexion.");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [tenantId, tenant?.nom]);

  // ─── Callback : recharger la config après sauvegarde Paramètres ───
  const reloadConfig = useCallback(async () => {
    const { data, error } = await supabase
      .from("bv_kpi_rules")
      .select("code_regle, valeur")
      .eq("actif", true);
    if (error) { console.error("Erreur reload config:", error); return; }
    if (data) {
      const map = {};
      data.forEach((r) => (map[r.code_regle] = r.valeur));
      setClientConfig(map);
    }
  }, []);

  // ─── Écran de chargement ───
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: C.bg,
          fontFamily: font,
        }}
      >
        <div style={{ color: C.slate }}>Chargement…</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        fontFamily: font,
        background: C.bg,
        color: C.text,
      }}
    >
      <Sidebar
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        clientNom={clientNom}
        syncLastRun={tenant?.syncLastRun}
        nav={NAV}
        activePage={activePage}
        setActivePage={setActivePage}
        profile={profile}
        signOut={signOut}
      />

      <main style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
        {/* Erreur de chargement */}
        {loadError && (
          <StatusMessage
            type="error"
            message={loadError}
            action={{ label: "Réessayer", onClick: () => window.location.reload() }}
            style={{ marginBottom: 16 }}
          />
        )}
        {/* Avertissement sync périmée */}
        {tenant?.syncLastRun && (Date.now() - new Date(tenant.syncLastRun).getTime()) > 3 * 86400000 && (
          <StatusMessage
            type="warning"
            message="Les données n'ont pas été synchronisées depuis plus de 3 jours."
            detail={`Dernière synchronisation : ${new Date(tenant.syncLastRun).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}`}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Pages — chaque page est autonome et charge ses propres données */}
        {activePage === "direction" && ["admin", "dirigeant"].includes(role) && <PageDirection />}
        {activePage === "ca" && <PageCA />}
        {activePage === "commercial" && ["admin", "dirigeant"].includes(role) && <PageCommercial />}
        {activePage === "pilotage-chantier" && <PagePilotageChantier />}
        {activePage === "rh" && ["admin", "dirigeant"].includes(role) && <PageRH />}
        {activePage === "alertes" && ["admin", "dirigeant"].includes(role) && <PageAlertes />}
        {activePage === "aide" && <PageAide />}
        {activePage === "parametres" && ["admin", "dirigeant"].includes(role) && (
          <PageParametres tenantId={tenantId} role={role} onConfigSaved={reloadConfig} />
        )}
        {activePage === "equipe" && ["admin", "dirigeant"].includes(role) && (
          <PageEquipe tenantId={tenantId} tenant={tenant} role={role} profile={profile} />
        )}
      </main>
    </div>
  );
}